import React, { useRef, useState } from "react";
import Modal from "components/modal";
import { 
  MdPerson, 
  MdEmail, 
  MdPermIdentity,
  MdLocationOn,
  MdPhone, 
  MdCalendarToday,
  MdSchool,
  MdAssignment,
  MdVerified,
  MdLock,
  MdLockOpen,
  MdEdit, 
  MdDelete,
  MdPhotoCamera
} from "react-icons/md";

const UserDetailModal = ({ 
  isOpen, 
  onClose, 
  user, 
  onEdit, 
  onDelete, 
  onActivate, 
  onDeactivate,
  onUpdateAvatar,
  loading = false
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  const membre = user.membre || {};
  
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    } catch (error) {
      return dateString || "—";
    }
  };
  
  const handlePhotoClick = () => {
    fileInputRef.current.click();
  };
  
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier le type de fichier
      if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/)) {
        alert("Seules les images sont acceptées (JPEG, PNG, GIF, WebP)");
        return;
      }
      
      // Vérifier la taille du fichier
      if (file.size > 2 * 1024 * 1024) {
        alert("L'image est trop volumineuse (max 2MB)");
        return;
      }
      
      setUploading(true);
      
      try {
        await onUpdateAvatar(file);
      } catch (error) {
        console.error("Erreur lors de la mise à jour de la photo:", error);
      } finally {
        setUploading(false);
      }
    }
  };

  if (!user) return null;
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} width="lg">
      <div>
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center">
            <div className="relative group">
              {user.photo ? (
                <img
                  src={`${process.env.REACT_APP_API_URL}/uploads/${user.photo}`}
                  alt={`Avatar de ${user.username}`}
                  className="h-20 w-20 rounded-full object-cover border-4 border-white shadow-md"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/80?text=?";
                  }}
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xl font-medium border-4 border-white shadow-md">
                  {user.username ? user.username.charAt(0).toUpperCase() : '?'}
                </div>
              )}
              
              <button
                onClick={handlePhotoClick}
                className="absolute -bottom-1 -right-1 rounded-full bg-indigo-600 p-1.5 text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                title="Modifier la photo"
              >
                <MdPhotoCamera className="h-4 w-4" />
              </button>
              
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileChange}
              />
            </div>
            <div className="ml-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {membre.nom_prenom_membre || user.username}
              </h2>
              <div className="flex flex-wrap items-center text-sm text-gray-600 gap-2 mt-1">
                <span className="flex items-center">
                  <MdPermIdentity className="mr-1 h-4 w-4" />
                  @{user.username}
                </span>
                
                {user.role && (
                  <span 
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                      ${user.role.privilege === 1 ? 'bg-red-100 text-red-800' : 
                        user.role.privilege === 2 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-blue-100 text-blue-800'}`}
                  >
                    {user.role.role_name}
                  </span>
                )}
                
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user.isActive 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                }`}>
                  {user.isActive ? 'Actif' : 'Inactif'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            {user.isActive ? (
              <button
                onClick={onDeactivate}
                disabled={loading || uploading}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <MdLock className="h-4 w-4" />
                <span>Désactiver</span>
              </button>
            ) : (
              <button
                onClick={onActivate}
                disabled={loading || uploading}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-green-600 bg-green-100 hover:bg-green-200 transition-colors"
              >
                <MdLockOpen className="h-4 w-4" />
                <span>Activer</span>
              </button>
            )}
          </div>
        </div>

        <div className="h-1 w-16 bg-indigo-600 mb-6 rounded-full"></div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-4">Informations personnelles</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <MdPerson className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Nom et prénom</h4>
                  <p className="text-base text-gray-900">{membre.nom_prenom_membre || "—"}</p>
                </div>
              </div>

              <div className="flex items-start">
                <MdLocationOn className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Commune d'origine</h4>
                  <p className="text-base text-gray-900">{membre.com_origin || "—"}</p>
                </div>
              </div>

              <div className="flex items-start">
                <MdPhone className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Téléphone</h4>
                  <p className="text-base text-gray-900">{membre.tel_membre || "—"}</p>
                </div>
              </div>

              <div className="flex items-start">
                <MdCalendarToday className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Date de naissance</h4>
                  <p className="text-base text-gray-900">{formatDate(membre.date_naissance)}</p>
                </div>
              </div>

              <div className="flex items-start">
                <MdLocationOn className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Adresse</h4>
                  <p className="text-base text-gray-900">{membre.adresse_membre || "—"}</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-4">Informations de compte</h3>
            <div className="space-y-4">
                            <div className="flex items-start">
                <MdEmail className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Email</h4>
                  <p className="text-base text-gray-900">{user.email || "—"}</p>
                </div>
              </div>

              <div className="flex items-start">
                <MdPermIdentity className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Nom d'utilisateur</h4>
                  <p className="text-base text-gray-900">@{user.username || "—"}</p>
                </div>
              </div>

              <div className="flex items-start">
                <MdVerified className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Statut du compte</h4>
                  <p className={`text-base ${user.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {user.isActive ? 'Actif' : 'Désactivé'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <MdAssignment className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Rôle</h4>
                  <p className="text-base text-gray-900">
                    {user.role ? `${user.role.role_name}${user.role.role_description ? ` (${user.role.role_description})` : ''}` : "—"}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <MdSchool className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Informations académiques</h4>
                  <div className="text-base text-gray-900">
                    <p>Matricule: {membre.num_matricule || "—"}</p>
                    <p>CIN: {membre.cin_membre || "—"}</p>
                    {membre.promotion && (
                      <p>Promotion: {membre.promotion.name_prom}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={onEdit}
            disabled={loading || uploading}
            className="inline-flex items-center px-4 py-2 rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 border border-transparent transition-colors"
          >
            <MdEdit className="mr-2 h-4 w-4" />
            Modifier
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={loading || uploading}
            className="inline-flex items-center px-4 py-2 rounded-md text-red-700 bg-red-100 hover:bg-red-200 border border-transparent transition-colors"
          >
            <MdDelete className="mr-2 h-4 w-4" />
            Supprimer
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
            onClick={onClose}
            disabled={loading || uploading}
          >
            Fermer
          </button>
        </div>
        
        {(loading || uploading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UserDetailModal;